import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import usePageMeta from "@/hooks/usePageMeta";
import { allWorks, formatHeat, getFeaturedWorks, sortWorks, TYPE_LABELS } from "@/lib/works";

const KEYWORDS = ["悬疑", "社会派", "犯罪", "心理", "群像", "剧情"];

export default function Home() {
  usePageMeta({
    title: "痴汉电车-精彩剧情介绍与影视作品推荐",
    description:
      "痴汉电车网站提供丰富的影视内容，涵盖相关电影、电视剧剧情简介与推荐，帮助用户全面了解痴汉电车相关作品。",
    keywords: "痴汉电车,痴汉电车影视,痴汉电车剧情,痴汉电车电影,痴汉电车电视剧",
    suffix: false,
  });

  const featured = getFeaturedWorks();
  const latest = sortWorks(allWorks, "updated").slice(0, 8);
  const topRated = sortWorks(allWorks, "rating").slice(0, 4);
  const movieCount = allWorks.filter((work) => work.type === "movie").length;
  const tvCount = allWorks.filter((work) => work.type === "tv").length;

  const entries = [
    {
      type: "movie",
      title: "电影",
      subtitle: "两小时一次的完整冲击",
      count: movieCount,
      image:
        "https://readdy.ai/api/search-image?query=cinematic%20dark%20movie%20theater%20screen%20glowing%20deep%20crimson%2C%20empty%20velvet%20seats%20in%20foreground%2C%20dusty%20projector%20light%20beam%2C%20charcoal%20black%20atmosphere%2C%20film%20grain%2C%20dramatic%20contrast%2C%20no%20text&width=1200&height=800&seq=chikan-entry-movie&orientation=landscape",
    },
    {
      type: "tv",
      title: "电视剧",
      subtitle: "一集一集慢慢收网",
      count: tvCount,
      image:
        "https://readdy.ai/api/search-image?query=cinematic%20dark%20living%20room%20at%20night%20with%20a%20television%20casting%20crimson%20red%20glow%2C%20long%20drama%20series%20atmosphere%2C%20charcoal%20black%20shadows%2C%20rain%20against%20the%20window%2C%20film%20grain%2C%20moody%20high%20contrast%2C%20no%20text&width=1200&height=800&seq=chikan-entry-tv&orientation=landscape",
    },
  ];

  return (
    <SiteLayout>
      <h1 className="sr-only">痴汉电车 - 精彩剧情介绍与影视作品推荐</h1>
      <HeroCarousel slides={featured} />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {entries.map((entry) => (
              <Link
                key={entry.type}
                to={`/category/${entry.type}`}
                className="group relative block h-[220px] md:h-[260px] rounded-lg overflow-hidden border border-background-200/70 cursor-pointer"
              >
                <img
                  src={entry.image}
                  alt={`${entry.title}分类入口`}
                  title={`痴汉电车${entry.title}分类`}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-md bg-primary-500/20 border border-primary-500/40 flex items-center justify-center">
                      <i className={`${entry.type === "movie" ? "ri-film-line" : "ri-tv-2-line"} text-primary-300 text-xl`} />
                    </span>
                    <span className="font-label text-[11px] tracking-[0.28em] text-foreground-600 uppercase">
                      {entry.type === "movie" ? "FEATURE FILM" : "SERIES"}
                    </span>
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">{entry.title}</h2>
                    <p className="mt-2 text-sm text-foreground-600">{entry.subtitle}</p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm text-primary-300 group-hover:text-primary-200">
                      共 {entry.count} 部作品
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 最新更新 */}
      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeading
            eyebrow="LATEST UPDATES"
            title="最新更新"
            description="按剧情资料入库时间排序，近期整理完成的作品与剧集。"
            action={{ label: "查看全部作品", to: "/category" }}
          />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {latest.map((work) => (
              <MovieCard key={work.id} work={work} />
            ))}
          </div>
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-8 lg:gap-14">
            <div>
              <div className="flex items-center gap-3">
                <span className="w-8 h-[2px] bg-accent-500" />
                <span className="font-label text-[11px] tracking-[0.28em] text-accent-300 uppercase">MOST WATCHED</span>
              </div>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
                热门推荐榜
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                综合站内收藏、剧情完整度与影迷讨论热度整理。适合第一次接触该题材的观众，从口碑最稳的一部开始。
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="rounded-md border border-background-200/70 p-4">
                  <p className="font-heading text-2xl md:text-3xl text-primary-300">{allWorks.length}</p>
                  <p className="mt-1 text-xs text-foreground-600">已收录作品</p>
                </div>
                <div className="rounded-md border border-background-200/70 p-4">
                  <p className="font-heading text-2xl md:text-3xl text-accent-400">4.8</p>
                  <p className="mt-1 text-xs text-foreground-600">平均剧情评分</p>
                </div>
              </div>
              <Link
                to="/category?sort=rating"
                className="mt-8 inline-flex items-center gap-2 text-sm text-foreground-800 hover:text-primary-300 transition-colors"
              >
                查看完整评分排行
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <ul className="flex flex-col divide-y divide-background-200/70">
              {topRated.map((work, rank) => (
                <li key={work.id}>
                  <Link
                    to={`/detail/${work.id}`}
                    className="group flex items-center gap-4 py-4 first:pt-0 last:pb-0 cursor-pointer"
                  >
                    <span className="font-heading text-2xl md:text-3xl text-background-500 w-8 shrink-0 group-hover:text-primary-400 transition-colors">
                      {String(rank + 1).padStart(2, "0")}
                    </span>
                    <div className="w-14 h-20 md:w-16 md:h-24 rounded-md overflow-hidden shrink-0 bg-background-50">
                      <img
                        src={work.poster}
                        alt={`${work.title} 海报`}
                        title={`${work.title} 剧情介绍`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading text-base md:text-lg text-foreground-950 group-hover:text-primary-300 transition-colors truncate">
                        {work.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-600">
                        {TYPE_LABELS[work.type]} · {work.year} · {work.region}
                      </p>
                      <p className="mt-1 text-xs text-foreground-500 truncate">{work.genres.join(" / ")}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-label text-base md:text-lg text-accent-400">{work.rating.toFixed(1)}</p>
                      <p className="text-[11px] text-foreground-500">{formatHeat(work.heat)} 热度</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 关键词 */}
      <section className="w-full px-4 md:px-8 py-4 md:py-8">
        <div className="max-w-[1400px] mx-auto border-y border-background-200/70 py-8 flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="shrink-0">
            <p className="font-label text-[11px] tracking-[0.28em] text-foreground-500 uppercase">STORY KEYWORDS</p>
            <p className="mt-2 text-sm text-foreground-700">按剧情关键词快速筛选</p>
          </div>
          <div className="flex flex-wrap gap-2 lg:ml-auto">
            {KEYWORDS.map((keyword) => (
              <Link
                key={keyword}
                to={`/search?q=${encodeURIComponent(keyword)}`}
                className="px-4 py-2 rounded-full bg-secondary-900/60 border border-secondary-800 text-sm text-foreground-700 hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                # {keyword}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden border border-background-200/70">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20dark%20archive%20room%20filled%20with%20vintage%20film%20reels%20and%20stacked%20posters%2C%20warm%20crimson%20desk%20lamp%20light%2C%20deep%20charcoal%20shadows%2C%20dust%20particles%20in%20the%20air%2C%20film%20grain%2C%20nostalgic%20high%20contrast%20atmosphere%2C%20no%20text&width=1200&height=900&seq=chikan-about-01&orientation=landscape"
              alt="痴汉电车影视剧情档案室"
              title="痴汉电车影视剧情档案"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 bg-background-50/85 backdrop-blur rounded-md px-4 py-3 border border-background-200/70">
              <i className="ri-film-line text-primary-400 text-lg" />
              <p className="text-xs text-foreground-700">剧情档案 · 已整理 22 部作品 / 6 类剧情标签</p>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-primary-500" />
              <span className="font-label text-[11px] tracking-[0.28em] text-primary-300 uppercase">ABOUT THE SITE</span>
            </div>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              一部一部，把剧情讲清楚
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              痴汉电车是一个聚焦该题材影视作品的剧情档案站。我们不提供在线播放，只做一件事：把每部作品的剧情、结构、冲突与看点写清楚，让你在按下播放键之前就知道它值不值得看。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              作品按电影与电视剧分类，支持年份、地区、剧情类型多重筛选；每一条简介都与详情页内容一一对应，不做拼凑式标题党。
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { value: "22", label: "收录作品" },
                { value: "6", label: "剧情标签" },
                { value: "周更", label: "资料更新" },
              ].map((stat) => (
                <div key={stat.label} className="border-l-2 border-primary-500/60 pl-4">
                  <p className="font-heading text-xl md:text-2xl text-foreground-950">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-300/70 text-sm text-foreground-800 hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
            >
              了解更多
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}